import React from 'react';

/**
 * Shared data-table primitives.
 *
 * Why this exists: the list pages previously hand-rolled each row as a CSS grid
 * with hardcoded pixel columns (e.g. `grid-cols-[8px_minmax(200px,1fr)_90px_…]`).
 * That had two defects, both measured on the running app:
 *
 *  1. Fixed pixel tracks cannot fit localized content. `AGENDAMENTO` needs 114px
 *     and was given 92px, so it ellipsized in all 50 rows at every viewport
 *     >= 1280px. `Localização Personalizada` needs 187px for 92px.
 *  2. The grids were gated behind `hidden xl:grid`, so between 1024px and 1279px
 *     the table disappeared entirely and the page fell back to a stacked card
 *     layout — a dead zone on 1280x800 and 1366x768 laptops once the 228px
 *     sidebar is subtracted.
 *
 * Real `<table>` layout fixes both: the browser sizes columns from content,
 * `table-fixed` + explicit widths on the numeric columns keeps them aligned, and
 * the markup degrades to a horizontal scroll instead of vanishing.
 *
 * Styling contract: header cells use `surface-2` (a decorative divider), numeric
 * columns are right-aligned and tabular-figured so magnitudes are comparable at a
 * glance, and the identity column is left-aligned and truncates with a title
 * attribute rather than silently clipping.
 */

export type TableAlign = 'left' | 'right' | 'center';

export interface Column<Row> {
  /** Stable key, also used as the React key. */
  key: string;
  /** Header content. */
  header: React.ReactNode;
  /** Cell renderer. */
  cell: (row: Row, index: number) => React.ReactNode;
  /** Text alignment for both header and body. Defaults to `left`. */
  align?: TableAlign;
  /** Tailwind width class, e.g. `w-32`. Applied to the header cell. */
  width?: string;
  /** Hide below this breakpoint. */
  hideBelow?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Extra classes for body cells in this column. */
  cellClassName?: string;
}

const alignClass: Record<TableAlign, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

const hideClass = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
  xl: 'hidden xl:table-cell',
  '2xl': 'hidden 2xl:table-cell',
} as const;

export interface DataTableProps<Row> {
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row, index: number) => string;
  /** Left accent stripe, rendered as the first cell of every row. */
  rowAccent?: (row: Row, index: number) => string;
  onRowClick?: (row: Row) => void;
  /** Rendered inside the table when `rows` is empty. */
  emptyState?: React.ReactNode;
  /** Accessible description of the table. */
  caption?: string;
  className?: string;
}

/**
 * A semantic, content-sized table.
 *
 * Wrapped in its own horizontal scroll container so narrow viewports degrade to
 * scrolling rather than clipping. The wrapper keeps `min-w` off the table itself
 * so columns still shrink to content on wide screens.
 */
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  rowAccent,
  onRowClick,
  emptyState,
  caption,
  className = '',
}: DataTableProps<Row>) {
  const totalCols = columns.length + (rowAccent ? 1 : 0);

  return (
    <div className="overflow-x-auto custom-scrollbar rounded-xl border border-surface-border-light dark:border-surface-border-dark">
      <table className={`w-full border-collapse text-sm ${className}`}>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="bg-surface-2-light dark:bg-surface-2-dark">
            {rowAccent && <th scope="col" className="w-2 p-0" aria-label="" />}
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={`px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark ${
                  alignClass[col.align ?? 'left']
                } ${col.width ?? ''} ${col.hideBelow ? hideClass[col.hideBelow] : ''}`}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={totalCols} className="px-4 py-12 text-center">
                {emptyState ?? '—'}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr
                key={rowKey(row, index)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`group transition-colors ${
                  onRowClick ? 'cursor-pointer' : ''
                } hover:bg-surface-2-light dark:hover:bg-surface-2-dark`}
              >
                {rowAccent && (
                  <td className="p-0">
                    <div className={`h-full min-h-[3.25rem] w-2 ${rowAccent(row, index)}`} />
                  </td>
                )}
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-4 py-3 align-middle text-slate-700 dark:text-slate-200 ${
                      alignClass[col.align ?? 'left']
                    } ${col.hideBelow ? hideClass[col.hideBelow] : ''} ${col.cellClassName ?? ''}`}
                  >
                    {col.cell(row, index)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Primary cell: an identity (name/title) over a secondary meta line.
 * Truncates with a tooltip rather than clipping mid-glyph.
 */
export function TablePrimaryCell({
  title,
  meta,
  badge,
  trailing,
}: {
  title: React.ReactNode;
  meta?: React.ReactNode;
  badge?: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 items-center gap-2">
        <span
          className="truncate text-sm font-bold text-slate-900 dark:text-white"
          title={typeof title === 'string' ? title : undefined}
        >
          {title}
        </span>
        {badge}
        {trailing}
      </div>
      {meta && (
        <div className="flex min-w-0 items-center gap-3 text-[11px] font-semibold uppercase tracking-wider text-text-subtle-light dark:text-text-subtle-dark">
          {meta}
        </div>
      )}
    </div>
  );
}

/**
 * Numeric cell: right-aligned, tabular figures so magnitudes line up.
 * The unit is rendered inline at the same size so the digits themselves share a
 * right edge across rows (a smaller suffix makes the numbers jog).
 */
export function TableNumericCell({
  value,
  unit,
  hint,
  tone,
}: {
  value: React.ReactNode;
  unit?: string;
  hint?: React.ReactNode;
  tone?: string;
}) {
  return (
    <div className="flex flex-col items-end gap-0.5">
      <span
        className={`text-sm font-bold tabular-nums whitespace-nowrap ${
          tone ?? 'text-slate-700 dark:text-slate-200'
        }`}
      >
        {value}
        {unit && <span className="ml-1 font-semibold">{unit}</span>}
      </span>
      {hint && (
        <span className="text-[10px] font-medium whitespace-nowrap text-text-subtle-light dark:text-text-subtle-dark">
          {hint}
        </span>
      )}
    </div>
  );
}
